import { getMemberByIdV2, updateUmsMemberV2 } from 'apis/ums';
import { useQuery } from '@tanstack/react-query';
import { Button, Input, message, Select, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import { getLocalStorageLanguage } from 'commons';
import { findLabelByValue } from 'commons/options';
import { useToggle } from 'react-use';
import { selectGlobalInfo } from 'store/slices/globalInfoSlice';
import { useAppSelector } from 'store/hooks';
import { SelectOption } from 'types/base';
import { Edit, Save, X } from 'lucide-react';
import UmsMemberTagEditor from 'components/ums-member-tag-editor';
import { useCityList } from 'apis/home';
import { LANGUAGE_MAP } from 'constant/language';

type Member = Awaited<ReturnType<typeof getMemberByIdV2>>;
type UserEditData = Required<
  Pick<
    Member,
    | 'firstName'
    | 'lastName'
    | 'countryCode'
    | 'phone'
    | 'country'
    | 'city'
    | 'detailAddress'
    | 'idCertificate'
    | 'paymentDetails'
    | 'remark'
    | 'email'
    | 'paymentDetails'
  >
>;

export const MemberInfo = ({
  memberId,
  onSaveSuccess,
}: {
  memberId?: number;
  onSaveSuccess: (data: UserEditData) => void;
}) => {
  const {
    data: member,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['member-detail', memberId],
    queryFn: () => getMemberByIdV2(memberId!),
    enabled: !!memberId,
  });

  const [userEditData, setUserEditData] = useState<UserEditData>({
    firstName: '',
    lastName: '',
    countryCode: '',
    phone: '',
    country: '',
    city: '',
    detailAddress: '',
    idCertificate: '',
    paymentDetails: '',
    remark: '',
    email: '',
  });
  useEffect(() => {
    if (member) {
      setUserEditData({
        firstName: member.firstName,
        lastName: member.lastName,
        countryCode: member.countryCode || '',
        phone: member.phone,
        country: member.country,
        city: member.city || '',
        detailAddress: member.detailAddress || '',
        idCertificate: member.idCertificate || '',
        paymentDetails: member.paymentDetails || '',
        remark: member.remark || '',
        email: member.email,
      });
    }
  }, [member]);

  const [isEditing, toggleIsEditing] = useToggle(false);
  const [isSaving, setIsSaving] = useState(false);
  const { countryOptions, countryCodeOptions } =
    useAppSelector(selectGlobalInfo);
  const { cityList } = useCityList(member?.country);
  const { cityList: editCityList, loading: editCityListLoading } = useCityList(
    userEditData.country,
  );

  const baseInfoList = useMemo(() => {
    if (!member) {
      return [];
    }

    const isEn = getLocalStorageLanguage() === LANGUAGE_MAP.EN;
    let list: {
      label: string | React.ReactNode;
      node: React.ReactNode;
      key: string;
    }[] = [];

    const {
      firstName,
      lastName,
      email,
      countryCode,
      phone,
      country,
      detailAddress,
      idCertificate,
      paymentDetails,
      city,
      remark,
    } = member;

    if (isEn) {
      list = [
        {
          key: 'first_name',
          label: <Trans i18nKey={LOCALS.first_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, firstName: e.target.value })
              }
            />
          ) : (
            firstName || '-'
          ),
        },
        {
          key: 'last_name',
          label: <Trans i18nKey={LOCALS.last_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, lastName: e.target.value })
              }
            />
          ) : (
            lastName || '-'
          ),
        },
      ];
    } else {
      list = [
        {
          key: 'last_name',
          label: <Trans i18nKey={LOCALS.last_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.lastName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, lastName: e.target.value })
              }
            />
          ) : (
            lastName || '-'
          ),
        },
        {
          key: 'first_name',
          label: <Trans i18nKey={LOCALS.first_name} />,
          node: isEditing ? (
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.firstName}
              onChange={(e) =>
                setUserEditData({ ...userEditData, firstName: e.target.value })
              }
            />
          ) : (
            firstName || '-'
          ),
        },
      ];
    }

    list = [
      ...list,
      {
        key: 'email',
        label: <Trans i18nKey={LOCALS.email} />,
        node: email ? (
          <a
            href={`/ums/member-view/${memberId}`}
            target="_blank"
            rel="noreferrer"
          >
            {email}
          </a>
        ) : (
          '-'
        ),
      },
      {
        key: 'phone',
        label: <Trans i18nKey={LOCALS.phone_number} />,
        node: isEditing ? (
          <div className="flex items-center gap-2 md:flex-row flex-col">
            <Select
              className="w-full"
              placeholder={<Trans i18nKey={LOCALS.please_select} />}
              style={{
                minWidth: 128,
              }}
              showSearch
              filterOption={(input: string, option: SelectOption | undefined) =>
                (option?.label ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
              options={countryCodeOptions}
              value={userEditData.countryCode}
              onChange={(countryCode) =>
                setUserEditData({ ...userEditData, countryCode })
              }
            />
            <Input
              placeholder={i18n.t(LOCALS.please_enter) || ''}
              value={userEditData.phone}
              onChange={(e) =>
                setUserEditData({ ...userEditData, phone: e.target.value })
              }
            />
          </div>
        ) : (
          <div>
            {countryCode && <span>+{countryCode} </span>}{' '}
            <span>{phone || '-'}</span>
          </div>
        ),
      },
      {
        key: 'country_region',
        label: <Trans i18nKey={LOCALS.country_region} />,
        node: isEditing ? (
          <Select
            className="w-full"
            showSearch
            filterOption={(input: string, option: SelectOption | undefined) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            options={countryOptions}
            value={userEditData.country}
            onChange={(country) =>
              setUserEditData({ ...userEditData, country, city: '' })
            }
            placeholder={<Trans i18nKey={LOCALS.please_select} />}
          />
        ) : (
          findLabelByValue(country, countryOptions)
        ),
      },
      {
        key: 'state_province_city',
        label: <Trans i18nKey={LOCALS.state_province_city} />,
        node: isEditing ? (
          <Select
            disabled={editCityListLoading}
            placeholder={i18n.t(LOCALS.please_select) || ''}
            showSearch
            filterOption={(input: string, option: SelectOption | undefined) =>
              (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
            }
            className="w-full"
            options={editCityList
              .map(({ name, id }) => {
                return {
                  value: id.toString(),
                  label: name,
                };
              })
              .sort((a, b) => a.label.localeCompare(b.label))}
            value={userEditData.city}
            onChange={(city) => setUserEditData({ ...userEditData, city })}
          />
        ) : city ? (
          <div className="break-words">
            {editCityList.find(({ id }) => String(id) === String(city))?.name ||
              cityList.find(({ id }) => String(id) === String(city))?.name ||
              city}
          </div>
        ) : (
          '-'
        ),
      },
      {
        key: 'detail_address',
        label: <Trans i18nKey={LOCALS.detail_address} />,
        node: isEditing ? (
          <Input.TextArea
            placeholder={i18n.t(LOCALS.please_enter) || ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={userEditData.detailAddress}
            onChange={(e) =>
              setUserEditData({
                ...userEditData,
                detailAddress: e.target.value,
              })
            }
          />
        ) : (
          <div className="whitespace-pre-wrap break-words">
            {detailAddress || '-'}
          </div>
        ),
      },
      {
        key: 'id_certificate',
        label: <Trans i18nKey={LOCALS.id_certificate} />,
        node: isEditing ? (
          <Input.TextArea
            placeholder={i18n.t(LOCALS.please_enter) || ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={userEditData.idCertificate}
            onChange={(e) =>
              setUserEditData({
                ...userEditData,
                idCertificate: e.target.value,
              })
            }
          />
        ) : (
          <div className="whitespace-pre-wrap break-words">
            {idCertificate || '-'}
          </div>
        ),
      },
      {
        key: 'payment_details',
        label: <Trans i18nKey={LOCALS.payment_details} />,
        node: isEditing ? (
          <Input.TextArea
            placeholder={i18n.t(LOCALS.please_enter) || ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={userEditData.paymentDetails}
            onChange={(e) =>
              setUserEditData({
                ...userEditData,
                paymentDetails: e.target.value,
              })
            }
          />
        ) : (
          <div className="whitespace-pre-wrap break-words">
            {paymentDetails || '-'}
          </div>
        ),
      },
      {
        key: 'remark',
        label: <Trans i18nKey={LOCALS.note} />,
        node: isEditing ? (
          <Input.TextArea
            placeholder={i18n.t(LOCALS.please_enter) || ''}
            autoSize={{ minRows: 2, maxRows: 6 }}
            value={userEditData.remark}
            onChange={(e) =>
              setUserEditData({
                ...userEditData,
                remark: e.target.value,
              })
            }
          />
        ) : (
          <div className="whitespace-pre-wrap break-words">{remark || '-'}</div>
        ),
      },
      {
        key: 'tags',
        label: <Trans i18nKey={LOCALS.khvsAbbLJs} />,
        node: (
          <UmsMemberTagEditor tags={member.tags || []} memberId={member.id} />
        ),
      },
    ];

    return list;
  }, [
    cityList,
    countryCodeOptions,
    countryOptions,
    editCityList,
    editCityListLoading,
    isEditing,
    member,
    memberId,
    userEditData,
  ]);

  if (!memberId) {
    return (
      <div className="flex justify-center items-center text-gray-500">
        {i18n.t(LOCALS.please_select_a_member)}
      </div>
    );
  }

  if (isLoading || !member) {
    return (
      <div className="flex justify-center items-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="relative flex gap-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-5 w-full">
        {baseInfoList.map((item) => {
          return (
            <div key={item.key}>
              <div className="text-xs font-medium text-gray-500">
                {item.label}
              </div>
              <div className="mt-1 text-sm font-medium">{item.node}</div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-2 self-start shrink-0">
        {isEditing ? (
          <Button
            className="flex items-center gap-2"
            onClick={() => toggleIsEditing(false)}
          >
            <X className="w-4 h-4" />
          </Button>
        ) : (
          <Button
            className="flex items-center gap-2"
            onClick={() => toggleIsEditing(true)}
          >
            <Edit className="w-4 h-4" />
          </Button>
        )}
        <Button
          type="primary"
          loading={isSaving}
          disabled={!isEditing || isSaving}
          className="flex items-center gap-2"
          onClick={async () => {
            setIsSaving(true);
            toggleIsEditing(false);
            await updateUmsMemberV2(memberId, userEditData);
            onSaveSuccess(userEditData);
            setIsSaving(false);
            message.success(i18n.t(LOCALS.successful_operation));
            refetch();
          }}
        >
          <Save className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
